import type {
  DemandSummaryGroup,
  DemandSummaryResponse,
} from "@/lib/data/requests/reports/reports-demand-summary";
import { ReportTable, count, type ReportColumn } from "../shared/ReportTable";
import { MonthlyDemandChart } from "./MonthlyDemandChart";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";

type Props = {
  report?: DemandSummaryResponse;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  onRetry: () => void;
};

export function DemandSummaryReport({
  report,
  isLoading,
  isFetching,
  isError,
  onRetry,
}: Props) {
  const data = report?.data;
  const columns: ReportColumn<DemandSummaryGroup>[] = [
    {
      label: "Team",
      value: (row) => row.label,
      render: (row) => row.label,
    },
    ...(
      [
        ["Prior day", "priorDay"],
        ["Week to date", "weekToDate"],
        ["Month to date", "monthToDate"],
        ["Prior month", "priorMonth"],
        ["Year to date", "yearToDate"],
      ] as const
    ).map(([label, key]) => ({
      label,
      numeric: true,
      value: (row: DemandSummaryGroup) => row[key],
      render: (row: DemandSummaryGroup) => count(row[key]),
    })),
  ];
  return (
    <div className="space-y-6" aria-busy={isFetching}>
      <div className="flex items-baseline justify-between gap-3">
        <h1 className="text-3xl font-semibold tracking-tight">Daily demands</h1>
        <span className="text-muted-foreground min-h-4 text-xs">
          {isFetching ? "Updating report…" : ""}
        </span>
      </div>
      <span role="status" aria-live="polite" className="sr-only">
        {isFetching ? "Updating report" : "Report updated"}
      </span>
      {isError ? (
        <div role="alert" className="p-8 text-sm">
          The report could not be loaded.{" "}
          <button className="underline" onClick={onRetry}>
            Try again
          </button>
        </div>
      ) : (
        <>
          {data &&
            (!data.freshness.ready ||
              data.freshness.pendingMatters > 0 ||
              data.freshness.missingMatters > 0) && (
              <div
                role="status"
                className="border-b bg-amber-50 px-5 py-3 text-sm text-amber-950"
              >
                Reporting is updating. {data.freshness.missingMatters} matters
                await their first projection and {data.freshness.pendingMatters}{" "}
                have pending changes. These figures may be incomplete.
              </div>
            )}
          <section>
            <ReportTable
              columns={columns}
              rows={data?.groups ?? []}
              total={data?.totals}
              rowKey={(row) => row.teamId ?? "unassigned"}
              isLoading={isLoading}
              tableName={tanstackTableNames.reportsDemandSummary}
            />
          </section>
          <section>
            <h2 className="mb-4 text-2xl font-semibold tracking-tight">
              Total demands sent by month
            </h2>
            {isLoading ? (
              <div className="text-muted-foreground flex h-80 items-center justify-center text-sm">
                Loading report…
              </div>
            ) : (
              <MonthlyDemandChart months={data?.months ?? []} />
            )}
          </section>
        </>
      )}
    </div>
  );
}
